import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    // Set by the challenge page, so someone resetting mid sign-up lands back
    // on the form rather than the home page. Read once, then cleared.
    const goToSignIn = () => {
        let back = '/';
        try {
            back = localStorage.getItem('reset_return') || '/';
            localStorage.removeItem('reset_return');
        } catch { /* private mode */ }
        navigate(back);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirm) { toast.error('Passwords do not match'); return; }
        if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        setLoading(true);
        try {
            await authApi.resetPassword(token, password);
            setDone(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Reset failed. The link may have expired.');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <CardTitle>Invalid link</CardTitle>
                        <CardDescription>This reset link is missing or malformed.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button className="w-full" onClick={() => navigate('/')}>Back to sign in</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <Card className="w-full max-w-md">
                {done ? (
                    <>
                        <CardHeader className="text-center">
                            <CardTitle>Password updated</CardTitle>
                            <CardDescription>You can now sign in with your new password.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Button className="w-full rounded-full bg-primary hover:bg-primary/90" onClick={goToSignIn}>
                                Go to sign in
                            </Button>
                        </CardContent>
                    </>
                ) : (
                    <>
                        <CardHeader className="text-center">
                            <CardTitle>Choose a new password</CardTitle>
                            <CardDescription>Enter and confirm your new password below</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="new-password">New password</Label>
                                    <div className="relative">
                                        <Input
                                            id="new-password"
                                            type={showPassword ? 'text' : 'password'}
                                            autoComplete="new-password"
                                            autoCapitalize="none"
                                            autoCorrect="off"
                                            spellCheck={false}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            className="bg-secondary pr-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(v => !v)}
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="confirm-password">Confirm password</Label>
                                    <Input
                                        id="confirm-password"
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        spellCheck={false}
                                        placeholder="••••••••"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        required
                                        className="bg-secondary"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full rounded-full bg-primary hover:bg-primary/90"
                                    disabled={loading}
                                >
                                    {loading ? 'Updating...' : 'Set new password'}
                                </Button>
                            </form>
                        </CardContent>
                    </>
                )}
            </Card>
        </div>
    );
};

export default ResetPassword;
